import { fromLatLon, toLatLon } from 'utm';
import { orderBy } from 'lodash';
import { KMLElement } from '../kml';
import { Sample, SampleBox, SoilCore } from '../db';
import { convertProjection4329 } from '../utils';
import { dispatchActiveBoxUpdated } from '../boxEvents';
import { dispatchCoresNeedUpdate, dispatchPathUpdated, dispatchSamplesUpdated } from '../mapEvents';
import { dispatchMissionUpdated } from '../missionEvents';
import { KMLFeature, KMLSampleFeatureProperties } from '../types/types';
import GeometryType from 'ol/geom/GeometryType';
import { DB_SAVE_REASONS } from '../services/RecoveryServerDbManager';
import { saveDbToRecoveryServer } from '../db/datamanager';
import { clearBearingsAround } from './waypoint_bearings';
import { directedBearingDeg, rotateAboutOrigin, shortestRotationDelta } from '../services/coreLineRotation';

export function to_kml(this: Sample, { use_pulled_locations = false } = {}) {
  const sampleSite = this.getSampleSite();
  const centroid = sampleSite.getSampleCentroid();

  if (!centroid) {
    return;
  }

  const placemark = KMLElement.element('Placemark');
  const placemark_vis = KMLElement.subelement(placemark, 'visibility');
  const sampleBox = this.getSampleBox();
  placemark_vis.setText('1');
  const nm = KMLElement.subelement(placemark, 'name');
  nm.setText(this.sample_id.toString());
  const point = KMLElement.subelement(placemark, 'Point');
  const coords = KMLElement.subelement(point, 'coordinates');

  if (use_pulled_locations && this.hasCoresPulled()) {
    const calculatedCoordinates = this.calculatePulledCoresCentroid();
    coords.setText(`${calculatedCoordinates[1]},${calculatedCoordinates[0]},0`);
  } else {
    coords.setText(`${centroid.lon},${centroid.lat},0`);
  }

  coords.setText(`${centroid.lon},${centroid.lat},0`);
  placemark.putExtendedData('centroid', true);
  placemark.putExtendedData('order', this.order);
  placemark.putExtendedData('previous_order', this.previous_order);
  placemark.putExtendedData('bag_id', this.bag_id);
  placemark.putExtendedData('original_sample_id', sampleSite.original_sample_id);
  placemark.putExtendedData('external_reference_id', sampleSite.external_reference_id);
  placemark.putExtendedData('sampled_at', new Date((this.sampled_at || 0.0) * 1000).toISOString());
  placemark.putExtendedData('scanned_at', new Date((this.scanned_at || 0.0) * 1000).toISOString());
  placemark.putExtendedData('pulled_at', new Date((this.pulled_at || 0.0) * 1000).toISOString());
  placemark.putExtendedData('sample_site_source', this.sample_site_source);

  const sampling_spec = this.getSamplingSpec();

  if (!sampling_spec) {
    throw new Error(`Sample ${this.sample_id} has no sampling spec`);
  }

  placemark.putExtendedData('spec_id', sampling_spec.spec_id);
  placemark.putExtendedData('cores', sampling_spec.cores);

  if (sampleBox) {
    placemark.putExtendedData('box_uid', sampleBox.uid);
    placemark.putExtendedData('operator', sampleBox.username);
  }

  placemark.putExtendedData('pattern_type', sampling_spec.pattern_type);
  placemark.putExtendedData('radius', sampling_spec.radius);
  placemark.putExtendedData('length', sampling_spec.length);
  placemark.putExtendedData('angle', sampling_spec.angle);
  placemark.putExtendedData('start_depth', sampling_spec.start_depth);
  placemark.putExtendedData('end_depth', sampling_spec.end_depth);
  placemark.putExtendedData('change_reason', this.change_reason);
  placemark.putExtendedData('change_type', this.change_type);
  placemark.putExtendedData('backup_history', this.backup_history);
  const style = KMLElement.subelement(placemark, 'styleUrl');
  style.setText('#centroid');

  return placemark;
}

export function to_feature(
  this: Sample,
  showBasedOnPulledCoreLocations: boolean = false,
): KMLFeature<KMLSampleFeatureProperties> | null {
  const sampleSite = this.getSampleSite();
  const centroid = sampleSite.getSampleCentroid();

  if (centroid) {
    const sampling_spec = this.getSamplingSpec();

    const feature: KMLFeature<KMLSampleFeatureProperties> = {
      type: 'Feature',
      geometry: {},
      properties: {
        name: this.sample_id || '',
        visibility: true,
        centroid: true,
        order: this.order,
        bag_id: this.bag_id || '',
        pulled_at: this.pulled_at,
        skipped: this.skipped_or_deleted,
        change_type: this.change_type,
        sample_site_source: this.sample_site_source,
        spec_id: sampling_spec?.spec_id || '',
        cores: sampling_spec?.cores || 0,
        pattern_type: sampling_spec?.pattern_type || 0,
        radius: sampling_spec?.radius || 0,
        length: sampling_spec?.length || 0,
        angle: sampling_spec?.angle || 0,
        start_depth: sampling_spec?.start_depth || 0,
        end_depth: sampling_spec?.end_depth || 0,
        site_order: sampleSite.sampleOrder(this),
      },
    };

    let coordinates = [centroid.lon, centroid.lat];

    if (showBasedOnPulledCoreLocations && this.hasCoresPulled()) {
      const calculatedCoordinates = this.calculatePulledCoresCentroid();
      coordinates = [calculatedCoordinates[1], calculatedCoordinates[0]];
    }

    feature.geometry = {
      type: GeometryType.POINT,
      coordinates: convertProjection4329(coordinates),
    };

    return feature;
  } else {
    return null;
  }
}

export async function setBarcode(this: Sample, barcode?: string, boxInstanceId?: number) {
  this.bag_id = barcode;

  if (!barcode) {
    this.SampleBox_id = undefined;
    this.pulled_at = 0;
    this.scanned_at = 0;
  } else {
    this.SampleBox_id = boxInstanceId;
    const time = new Date().getTime() / 1000;
    const mission = this.getSampleSite().getMission();

    if (mission) {
      mission.sample_date = time;
    }

    this.pulled_at = time;
    this.scanned_at = time;
    // this.getSoilCores().forEach((core) => core.pulled_at = time);
  }

  const box = SampleBox.get(boxInstanceId);

  if (box) {
    box.needsUpdated = true;
    dispatchActiveBoxUpdated();
  }

  // CentroidsLayer no longer listens to BOX_EVENTS.ACTIVE_UPDATED —
  // narrow the signal to the specific sample whose bag_id flipped so
  // only its centroid JSX gets rebuilt (green fill after scan, white
  // after clear).
  dispatchSamplesUpdated([this.sample_id]);

  saveDbToRecoveryServer(DB_SAVE_REASONS.SAMPLE_BARCODE_SET).catch((err) => {
    console.error('Failed to save DB to recovery server after sample barcode set', err);
  });
}

/**
 * Direction of this sample's core line in degrees (0 = east, CCW, in [0, 360)):
 * the bearing from the first core the path visits to the last, or null when the
 * sample does not define a line (fewer than two cores, or all cores in the same
 * spot).
 *
 * Read from the live core positions, so it reflects any manual editing. A half
 * turn (180°) names the same line but the opposite walking direction.
 */
export function get_core_line_direction_deg(this: Sample): number | null {
  const cores = coresInPathOrder(this);

  if (cores.length < 2) {
    return null;
  }

  return directedBearingDeg(coreGeometryPoint(cores[0]), coreGeometryPoint(cores[cores.length - 1]));
}

/**
 * Rotate this sample's core line to be walked at `targetDirectionDeg` about the
 * sample centroid, moving every core and its waypoint rigidly. The centroid
 * itself does not move; each core keeps its distance to it.
 *
 * The rotation is the short way round and may be a full half turn: setting the
 * opposite direction (200° for a line walked at 20°) leaves the line exactly
 * where it is and swaps its ends, so the robot enters from the other side.
 * Pinned waypoint bearings on the touched hops are cleared and the nav checksum
 * recalculated, exactly as for any hand edit.
 *
 * Returns false (leaving the sample untouched) when there is nothing to rotate:
 * fewer than two cores, no sample centroid, or a core already pulled — moving
 * sampled cores would rewrite recorded work.
 */
export async function rotate_core_line(this: Sample, targetDirectionDeg: number): Promise<boolean> {
  const centroid = this.getSampleSite()?.getSampleCentroid();
  const mission = this.getSampleSite()?.getMission();
  const cores = coresInPathOrder(this);

  if (!centroid || !mission || cores.length < 2 || this.hasCoresPulled()) {
    return false;
  }

  const currentDirectionDeg = get_core_line_direction_deg.bind(this)();

  if (currentDirectionDeg === null) {
    return false;
  }

  const deltaDeg = shortestRotationDelta(currentDirectionDeg, targetDirectionDeg);

  if (deltaDeg === 0) {
    return true;
  }

  const origin = fromLatLon(centroid.lat, centroid.lon);
  const rotated = rotateAboutOrigin(cores.map(coreGeometryPoint), { x: origin.easting, y: origin.northing }, deltaDeg);
  const movedWaypointNumbers: number[] = [];

  cores.forEach((core: SoilCore, i: number) => {
    const latLon = toLatLon(rotated[i].x, rotated[i].y, origin.zoneNum, origin.zoneLetter);
    core.lat = latLon.latitude;
    core.lon = latLon.longitude;

    const waypoint = core.getCorePoint()?.getWaypoint();

    if (waypoint) {
      waypoint.lat = latLon.latitude;
      waypoint.lon = latLon.longitude;
      movedWaypointNumbers.push(waypoint.waypoint_number);
    }
  });

  // The hops into and out of every rotated core are no longer the planned ones.
  clearBearingsAround(mission, movedWaypointNumbers);

  // call 'to_ros_msg' to re-calculate the nav checksum
  mission.to_ros_msg();

  dispatchMissionUpdated();
  // Cores moved → CorePointsLayer (and the path they sit on) must rebuild.
  dispatchCoresNeedUpdate();
  dispatchPathUpdated();

  return true;
}

/**
 * This sample's cores in the order the path visits them: by waypoint number
 * when every core is on the path (a Smart Path tour may run a cluster backwards
 * relative to its core numbers), otherwise by core number.
 */
function coresInPathOrder(sample: Sample): SoilCore[] {
  const cores = sample.getSoilCores();
  const waypointNumbers = cores.map((core) => core.getCorePoint()?.getWaypoint()?.waypoint_number);

  if (waypointNumbers.every((waypointNumber) => typeof waypointNumber === 'number')) {
    return cores
      .map((core, i) => ({ core, waypointNumber: waypointNumbers[i] as number }))
      .sort((a, b) => a.waypointNumber - b.waypointNumber)
      .map(({ core }) => core);
  }

  return orderBy(cores, ['core_number'], ['asc']);
}

/** A core's target position as a metric point (UTM easting/northing). */
function coreGeometryPoint(core: SoilCore) {
  const location = fromLatLon(core.lat, core.lon);

  return { x: location.easting, y: location.northing };
}
